import type { CaseRecord } from "@recobra/schemas/case";
import type {
  DocumentClassification,
  DocumentFailureReason,
  DocumentType,
  DocumentVideoAnalysis,
} from "@recobra/schemas/document";

import type { DisplayDocument } from "./DisplayDocument";

export type UploadPhase =
  | { status: "idle" }
  | { status: "requesting-url" }
  | { status: "uploading"; progress: number }
  | { status: "complete"; documentId: string }
  | { status: "error"; message: string; retryable?: boolean };

export type UploadFileEntry = {
  id: string;
  file: File;
  fileFingerprint: string;
  phase: UploadPhase;
  documentId?: string;
  classificationStatus?: "uploaded" | "classifying" | "classified" | "failed";
  failureReason?: DocumentFailureReason;
  classification?: DocumentClassification;
  videoAnalysis?: DocumentVideoAnalysis;
  classificationOverride?: {
    documentType: DocumentType;
    overriddenBy: string;
    overriddenAt: string;
  };
  isProblematic?: boolean;
  analysisNote?: string;
};

export type DocumentAnalysisItem = { documentId: string; note: string };

export type AnalysisEvent =
  | {
      event: "analysis_complete";
      case?: CaseRecord;
      documentAnalysis?: DocumentAnalysisItem[];
    }
  | {
      event: "analysis_failed";
      problematicDocumentIds: string[];
      case?: CaseRecord;
      documentAnalysis?: DocumentAnalysisItem[];
    };

export type AnalyseStatus = "idle" | "loading" | "error" | "flagged" | "changed";

export type UploadRow =
  | {
      kind: "document";
      id: string;
      doc: DisplayDocument;
    }
  | {
      kind: "upload";
      id: string;
      entry: UploadFileEntry;
      doc: DisplayDocument;
    };

export const getDocumentId = (row: UploadRow): string | undefined =>
  row.kind === "document" ? row.doc.id : row.entry.documentId;

export const isAnalysisEvent = (data: unknown): data is AnalysisEvent => {
  if (typeof data !== "object" || data === null) return false;

  return (
    "event" in data && (data.event === "analysis_complete" || data.event === "analysis_failed")
  );
};
